import PropTracker from "../../../prop-trackers/prop-tracker.js";
import { Combiner } from "../../../prop-tracker-utils/combiner.js";
import SinglePropChainTracker from "../../../prop-tracker-utils/single-prop-chain-tracker.js";
import PropChainTracker from "../../../prop-tracker-utils/prop-chain-tracker.js";

import ShallowObjectMerger from "../../../utils/shallow-object-merger.js";

import ParentTrackerAssigner from "./objects/parent-tracker-assigner.js";
import PlacementTrackerController from "./objects/placement-tracker-controller.js";
import VisibilityTrackerController from "./objects/visibility-tracker-controller.js";
import MorphTargetTrackerController from "./objects/morph-target-tracker-controller.js";

const MeshTypes = ["Mesh", "SkinnedMesh"];

class ObjectProperties {
    constructor(evtCtx, shlWrapCtx, shlEvtCtx, { parentAssigner, placementController, visibilityController, morphTargetController }) {
        this._evtCtx = evtCtx;

        this._morphObjSources = MeshTypes.map(type => new PropTracker());
        this._morphObjMerger = new Combiner((key) => new ShallowObjectMerger());
        for (const [i, source] of this._morphObjSources.entries()) {
            this._morphObjMerger.setSource(i, source);
        }

        this._parentDataTracker = new SinglePropChainTracker(evtCtx);
        this._trModDataTracker = new SinglePropChainTracker(evtCtx);
        this._visDataTracker = new SinglePropChainTracker(evtCtx);
        this._coordTracker = new SinglePropChainTracker(evtCtx);
        this._morphTargetTracker = new PropChainTracker(evtCtx);

        this._parentTrackAssigner = new ParentTrackerAssigner(evtCtx, shlWrapCtx, shlEvtCtx, parentAssigner);
        this._placementTrackCtrl = new PlacementTrackerController(evtCtx, shlWrapCtx, shlEvtCtx, placementController);
        this._visibilityTrackCtrl = new VisibilityTrackerController(evtCtx, shlWrapCtx, shlEvtCtx, visibilityController);
        this._morphTargetTrackCtrl = new MorphTargetTrackerController(this._morphTargetTracker, this._morphObjMerger, evtCtx, shlWrapCtx, shlEvtCtx, morphTargetController);
    }

    trackParents(parentData) {
        this._parentDataTracker.track(parentData && parentData.data, ["value"]);
    }

    trackTransformModifiers(trModData) {
        this._trModDataTracker.track(trModData && trModData.data, ["value"]);
    }

    trackVisibility(visData) {
        this._visDataTracker.track(visData && visData.data, ["value"]);
    }

    trackCoords(coordData) {
        this._coordTracker.track(coordData && coordData.data, ["value"]);
    }

    trackMorphTargets(morphTargetData) {
        this._morphTargetTracker.track(morphTargetData && morphTargetData.data, ["value"]);
    }

    trackObjects(objects) {
        if (objects) {
            this._parentTrackAssigner.trackObjects(objects.all.data, objects.all.events);
            this._placementTrackCtrl.trackObjects(objects.all.data, objects.all.events);
            this._visibilityTrackCtrl.trackObjects(objects.all.data, objects.all.events);

            for (const [i, type] of MeshTypes.entries()) {
                const { data, events } = objects.byType[type];
                this._morphObjSources[i].track(data, events);
            }
        } else {
            this._parentTrackAssigner.trackObjects();
            this._placementTrackCtrl.trackObjects();
            this._visibilityTrackCtrl.trackObjects();

            for (const source of this._morphObjSources) {
                source.track();
            }
        }
    }

    update() {
        this._parentDataTracker.updateIfNeeded(({ prop: { newValue } }) => {
            this._parentTrackAssigner.trackData(newValue, newValue ? this._evtCtx.getEvents(newValue) : null);
        });

        this._trModDataTracker.updateIfNeeded(({ prop: { newValue } }) => {
            this._visibilityTrackCtrl.trackTransformModifierFlags(newValue, newValue ? this._evtCtx.getEvents(newValue) : null);
        });

        this._visDataTracker.updateIfNeeded(({ prop: { newValue } }) => {
            this._visibilityTrackCtrl.trackVisibilityFlags(newValue, newValue ? this._evtCtx.getEvents(newValue) : null);
        });

        this._coordTracker.updateIfNeeded(({ prop: { newValue } }) => {
            this._placementTrackCtrl.trackData(newValue, newValue ? this._evtCtx.getEvents(newValue) : null);
        });

        this._parentTrackAssigner.update();
        this._placementTrackCtrl.update();
        this._visibilityTrackCtrl.update();
        this._morphTargetTrackCtrl.update();
    }
}

export default ObjectProperties;
